import { CartResultUnconfirmedError } from './cartResultUnconfirmedError';

export { CartResultUnconfirmedError } from './cartResultUnconfirmedError';

/**
 * Request-local Pinia stores are the coordination boundary. Never put customer
 * identity, cart payloads or pending promises into a process-wide string cache.
 */
export interface CartContextIdentity {
  cartId: string;
  customer: string;
  store: string;
  locale: string;
  currency: string;
}

interface PendingCartState {
  pendingMutations: number;
  needsReconciliation?: boolean;
}

interface Coordinator {
  tail: Promise<void>;
  revision: number;
  readSequences: Record<string, number>;
  unconfirmedContexts: CartContextIdentity[];
  fullRead?: {
    revision: number;
    sequence: number;
    identity: CartContextIdentity;
    contract: string;
    promise: Promise<unknown>;
  };
  snapshot?: {
    revision: number;
    readSequence: number;
    identity: CartContextIdentity;
    contract: string;
    value: unknown;
  };
  quantitySnapshot?: {
    identity: CartContextIdentity;
    value: unknown;
    quantity: number;
    contract: string;
    readSequence: number;
  };
  dataOwner?: {
    identity: CartContextIdentity;
    value: unknown;
    contract: string;
    readSequence: number;
  };
}

export interface CartMutationGuard {
  assertCurrent(): void;
  adoptCartId(cartId: string): void;
}

export interface CartReadToken {
  revision: number;
  sequence: number;
  channel: string;
  identity: CartContextIdentity;
}

const coordinators = new WeakMap<object, Coordinator>();

function coordinatorFor(store: object): Coordinator {
  let coordinator = coordinators.get(store);
  if (!coordinator) {
    coordinator = { tail: Promise.resolve(), revision: 0, readSequences: {}, unconfirmedContexts: [] };
    coordinators.set(store, coordinator);
  }
  return coordinator;
}

export class CartContextChangedError extends Error {
  constructor() {
    super('Your cart context changed. Refresh your cart and try again.');
    this.name = 'CartContextChangedError';
  }
}

function sameOwner(a: CartContextIdentity, b: CartContextIdentity): boolean {
  return a.customer === b.customer && a.store === b.store
    && a.locale === b.locale && a.currency === b.currency;
}

function sameContext(a: CartContextIdentity, b: CartContextIdentity): boolean {
  return a.cartId === b.cartId && sameOwner(a, b);
}

function isUnknownWriteResult(error: any): boolean {
  const status = error?.response?.status ?? error?.status;
  return error instanceof CartResultUnconfirmedError
    || status >= 500 || status === 408
    || !!error?.networkError
    || error?.name === 'AbortError'
    || (error?.isAxiosError === true && !error?.response)
    || ['ECONNABORTED', 'ETIMEDOUT', 'ECONNRESET', 'ERR_NETWORK', 'ERR_CANCELED'].includes(error?.code);
}

export function needsCartReconciliation(store: PendingCartState, current: CartContextIdentity): boolean {
  const needed = coordinatorFor(store).unconfirmedContexts.some((identity) => sameContext(identity, current));
  if (store.needsReconciliation !== needed) store.needsReconciliation = needed;
  return needed;
}

function markUnconfirmed(store: PendingCartState, identity: CartContextIdentity): void {
  // An unknown empty-cart creation is not an unknown product write. Without a
  // known cart ID there is no cart that a read could reconcile; do not lock it.
  if (!identity.cartId) return;
  const coordinator = coordinatorFor(store);
  coordinator.snapshot = undefined;
  coordinator.quantitySnapshot = undefined;
  coordinator.dataOwner = undefined;
  if (!coordinator.unconfirmedContexts.some((value) => sameContext(value, identity))) {
    coordinator.unconfirmedContexts.push({ ...identity });
  }
  needsCartReconciliation(store, identity);
}

export function markCartChanged(store: object): void {
  const coordinator = coordinatorFor(store);
  coordinator.revision += 1;
  coordinator.snapshot = undefined;
  coordinator.quantitySnapshot = undefined;
  coordinator.dataOwner = undefined;
}

/** Pure read-only epoch shared by request-local cart metadata consumers. */
export function getCartRevision(store: object): number {
  return coordinatorFor(store).revision;
}

/** Read-only barrier for intent already queued while an earlier tail settles. */
export async function waitForCartMutations(store: object): Promise<void> {
  const coordinator = coordinatorFor(store);
  let tail: Promise<void>;
  do {
    tail = coordinator.tail;
    // eslint-disable-next-line no-await-in-loop -- Observe the new tail; never execute or retry a write.
    await tail;
  } while (tail !== coordinator.tail);
}

export function beginCartRead(store: object, identity: CartContextIdentity, channel = 'cart'): CartReadToken {
  const coordinator = coordinatorFor(store);
  coordinator.readSequences[channel] = (coordinator.readSequences[channel] ?? 0) + 1;
  return {
    revision: coordinator.revision,
    sequence: coordinator.readSequences[channel],
    channel,
    identity: { ...identity },
  };
}

/** Default/empty query aliases select the same full-cart command. */
export function cartReadContractKey(params: {
  customQuery?: unknown;
  customHeaders?: unknown;
  realCart?: boolean;
} = {}): string | null {
  const query = params.customQuery;
  const defaultQuery = query == null || (typeof query === 'object' && Object.getPrototypeOf(query) === Object.prototype
    && (Object.keys(query).length === 0
      || (Object.keys(query).length === 1 && (query as { cart?: string }).cart === 'cart')));
  const normalize = (value: unknown): unknown => {
    if (value === null) return null;
    if (typeof value === 'string' || typeof value === 'boolean') return value;
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (Array.isArray(value)) return Array.from(value, normalize);
    if (typeof value === 'object' && Object.getPrototypeOf(value) === Object.prototype) {
      return Object.fromEntries(Object.keys(value).sort().map((key) => [key, normalize((value as Record<string, unknown>)[key])]));
    }
    throw new Error('Unsupported cart read contract');
  };
  try {
    return JSON.stringify(normalize({
      query: defaultQuery ? {} : query,
      headers: params.customHeaders ?? {},
      realCart: Boolean(params.realCart),
    }));
  } catch {
    // Unknown query/header objects remain usable, but are never shared.
    return null;
  }
}

/** Share only an actual in-flight full read; no settled value is returned here. */
export function shareCartRead<T>(
  store: PendingCartState,
  identity: CartContextIdentity,
  contract: string | null,
  force: boolean,
  execute: (read: CartReadToken) => Promise<T>,
): Promise<T> {
  const coordinator = coordinatorFor(store);
  const current = coordinator.fullRead;
  if (!force && contract && current && current.contract === contract
    && current.revision === coordinator.revision && current.sequence === coordinator.readSequences.cart
    && sameContext(current.identity, identity)) {
    return current.promise as Promise<T>;
  }
  const read = beginCartRead(store, identity);
  coordinator.snapshot = undefined;
  coordinator.quantitySnapshot = undefined;
  coordinator.fullRead = undefined;
  coordinator.dataOwner = undefined;
  const promise = execute(read);
  if (contract) {
    const entry = { revision: read.revision, sequence: read.sequence, identity: { ...identity }, contract, promise };
    coordinator.fullRead = entry;
    const clear = () => {
      if (coordinator.fullRead === entry) coordinator.fullRead = undefined;
    };
    // Rejection is still delivered to every caller; the cleanup branch never
    // creates an unobserved rejected promise or retries the request.
    promise.then(clear).catch(clear);
  }
  return promise;
}

export function getCartReadInFlight(
  store: object,
  identity: CartContextIdentity,
  contract: string | null,
): Promise<unknown> | undefined {
  const coordinator = coordinatorFor(store);
  const read = coordinator.fullRead;
  return contract && read && read.contract === contract && read.revision === coordinator.revision
    && read.sequence === coordinator.readSequences.cart && sameContext(read.identity, identity) ? read.promise : undefined;
}

export function rememberCartReadSnapshot(
  store: PendingCartState,
  identity: CartContextIdentity,
  contract: string | null,
  value: { id?: string; items?: unknown[] | null; total_quantity?: number },
  readSequence = coordinatorFor(store).readSequences.cart ?? 0,
): void {
  const coordinator = coordinatorFor(store);
  if (!contract || !identity.cartId || value?.id !== identity.cartId
    || !Array.isArray(value.items) || store.pendingMutations > 0
    || needsCartReconciliation(store, identity)) return;
  coordinator.snapshot = { revision: coordinator.revision, readSequence, identity: { ...identity }, contract, value };
  if (coordinator.dataOwner?.value === value) coordinator.dataOwner.readSequence = readSequence;
}

/** Opt-in child reads may reuse an already-applied cart, including null addresses. */
export function hasCurrentCartSnapshot(
  store: PendingCartState,
  identity: CartContextIdentity,
  contract: string | null,
  value: unknown,
): boolean {
  const coordinator = coordinatorFor(store);
  const { snapshot } = coordinator;
  return Boolean(contract && snapshot && snapshot.contract === contract
    && snapshot.revision === coordinator.revision && snapshot.value === value
    && snapshot.readSequence === (coordinator.readSequences.cart ?? 0)
    && sameContext(snapshot.identity, identity) && store.pendingMutations === 0
    && !needsCartReconciliation(store, identity));
}

/** Provenance for fields already held by Pinia, not a claim of a fresh full read. */
export function rememberCartDataOwner(
  store: PendingCartState,
  identity: CartContextIdentity,
  value: { id?: string },
  contract: string | null,
): void {
  const coordinator = coordinatorFor(store);
  coordinator.dataOwner = contract && identity.cartId && value?.id === identity.cartId
    ? { identity: { ...identity }, value, contract, readSequence: coordinator.readSequences.cart ?? 0 } : undefined;
}

export function hasCurrentCartData(
  store: PendingCartState,
  identity: CartContextIdentity,
  value: unknown,
  contract: string | null,
): boolean {
  const owner = coordinatorFor(store).dataOwner;
  return Boolean(contract && owner && owner.contract === contract && owner.value === value
    && owner.readSequence === (coordinatorFor(store).readSequences.cart ?? 0)
    && sameContext(owner.identity, identity) && store.pendingMutations === 0
    && !needsCartReconciliation(store, identity));
}

/** Mutation responses can confirm the badge count without masquerading as a full read. */
export function rememberConfirmedCartQuantity(
  store: PendingCartState,
  identity: CartContextIdentity,
  value: { id?: string; items?: unknown[] | null; total_quantity?: number },
  contract = cartReadContractKey(),
  readSequence = coordinatorFor(store).readSequences.cart ?? 0,
): void {
  if (!contract || !identity.cartId || value?.id !== identity.cartId || !Array.isArray(value.items)
    || typeof value.total_quantity !== 'number' || !Number.isFinite(value.total_quantity) || value.total_quantity < 0
    || needsCartReconciliation(store, identity)) return;
  coordinatorFor(store).quantitySnapshot = { identity: { ...identity }, value, quantity: value.total_quantity, contract, readSequence };
}

export function hasConfirmedCartQuantity(
  store: PendingCartState,
  identity: CartContextIdentity,
  value: { total_quantity?: number },
  contract = cartReadContractKey(),
): boolean {
  const snapshot = coordinatorFor(store).quantitySnapshot;
  return Boolean(contract && snapshot && snapshot.contract === contract
    && snapshot.readSequence === (coordinatorFor(store).readSequences.cart ?? 0)
    && snapshot.value === value && snapshot.quantity === value?.total_quantity
    && sameContext(snapshot.identity, identity) && store.pendingMutations === 0
    && !needsCartReconciliation(store, identity));
}

export function canApplyCartRead(
  store: PendingCartState,
  token: CartReadToken,
  current: CartContextIdentity,
  returnedCartId?: string,
): boolean {
  const coordinator = coordinatorFor(store);
  // An initial read may legitimately obtain/create a cart ID. It may not adopt
  // a different customer, store, language or currency while it is in flight.
  const initialCartResolved = !token.identity.cartId && !!returnedCartId
    && current.cartId === returnedCartId && sameOwner(token.identity, current);
  return (store.pendingMutations ?? 0) === 0
    && coordinator.revision === token.revision
    && coordinator.readSequences[token.channel] === token.sequence
    && (sameContext(token.identity, current) || initialCartResolved);
}

/** Partial address patches and quantity-only reads must never acknowledge an unknown write. */
export function acknowledgeCartRead(
  store: PendingCartState,
  token: CartReadToken,
  current: CartContextIdentity,
  returnedCartId?: string,
): boolean {
  if (token.channel !== 'cart' || !returnedCartId || returnedCartId !== current.cartId
    || !canApplyCartRead(store, token, current, returnedCartId)) return false;
  const coordinator = coordinatorFor(store);
  coordinator.unconfirmedContexts = coordinator.unconfirmedContexts
    .filter((identity) => !sameContext(identity, current));
  needsCartReconciliation(store, current);
  return true;
}

/** Queue intent once; unknown results pause later writes until a full-cart read. */
export function enqueueCartMutation<T>(
  store: PendingCartState,
  getIdentity: () => CartContextIdentity,
  operation: (guard: CartMutationGuard) => Promise<T>,
): Promise<T> {
  const coordinator = coordinatorFor(store);
  const expected = { ...getIdentity() };
  if (needsCartReconciliation(store, expected)) {
    return Promise.reject(new CartResultUnconfirmedError());
  }
  store.pendingMutations = (store.pendingMutations ?? 0) + 1;
  markCartChanged(store);

  const guard: CartMutationGuard = {
    assertCurrent() {
      if (!sameContext(expected, getIdentity())) throw new CartContextChangedError();
    },
    adoptCartId(cartId) {
      const current = getIdentity();
      if (!sameOwner(expected, current) || current.cartId !== cartId) {
        throw new CartContextChangedError();
      }
      expected.cartId = cartId;
    },
  };

  const started = coordinator.tail.then(async () => {
    guard.assertCurrent();
    if (needsCartReconciliation(store, expected)) throw new CartResultUnconfirmedError();
    try {
      const value = await operation(guard);
      guard.assertCurrent();
      return value;
    } catch (error) {
      // An old-context error must not trigger cleanup against a new session.
      guard.assertCurrent();
      if (isUnknownWriteResult(error)) {
        markUnconfirmed(store, expected);
        if (error instanceof CartResultUnconfirmedError) throw error;
        const unconfirmed = new CartResultUnconfirmedError();
        unconfirmed.code = error?.code;
        unconfirmed.status = error?.response?.status ?? error?.status;
        throw unconfirmed;
      }
      throw error;
    }
  });
  const result = started.finally(() => {
    coordinator.revision += 1;
    coordinator.snapshot = undefined;
    store.pendingMutations -= 1;
  });
  coordinator.tail = result.then(() => undefined, () => undefined);
  return result;
}
